'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';

import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';

import ButtonCTA from 'components/ui/ButtonCTA';
import Link from 'components/ui/Link';
import RichText from 'components/ui/RichText';
import Text from 'components/ui/Text';

import { getColor } from 'utils';

import styles from './TextEyebrow.module.scss';

const Eyebrow = ({ children, theme = 'normal', firstBlock, hasHeadline }) => {
    // If eyebrow is the only heading and it's the first block, use h1
    const EyebrowTag = firstBlock && !hasHeadline ? 'h1' : 'p';

    return (
        <Text.Theme
            config={{
                'makers-reserve': {
                    baseTheme: 'beverlyLabelMedium',
                    themes: {
                        large: 'beverlyLabelLarge',
                    },
                },
                default: {
                    baseTheme:
                        theme === 'display'
                            ? 'labelSmall'
                            : theme === 'hero'
                              ? 'labelMedium'
                              : 'labelLarge',
                    themes: { large: 'labelLarge' },
                },
            }}
            className={cx(styles.innerHeadline, {
                [styles.innerHeadlineDisplay]: theme === 'display',
            })}
            as={EyebrowTag}
        >
            {children}
        </Text.Theme>
    );
};

Eyebrow.propTypes = {
    theme: PropTypes.oneOf([
        'normal',
        'display',
        'masthead',
        'hero',
        'displaySmall',
        'displaySmallFixed',
        'displaySmallToLarge1',
        'displayLarge',
        'displayXLarge',
        'ultrabold',
        'makers-reserve',
        'bodyMedium',
    ]),
    children: PropTypes.any,
    firstBlock: PropTypes.bool,
    hasHeadline: PropTypes.bool,
};

const Headline = ({ children, theme = 'normal', fixWidows = true }) => {
    const textThemeConfig = {
        'makers-reserve': {
            baseTheme:
                {
                    ultrabold: 'makers30',
                    default: 'makers30',
                    displaySmallFixed: 'makers30',
                    bodyMedium: 'headingMedium',
                }[theme] || 'makers36',
            themes: {
                ultrabold: { medium: 'makers48' },
                bodyMedium: { medium: 'bodyXLarge' },
                default: { medium: 'makers36' },
                displaySmallFixed: { medium: 'makers36' },
            }[theme] || { medium: 'makers60' },
        },
        default: {
            baseTheme:
                {
                    displaySmallFixed: 'displaySmall',
                    displaySmallToLarge1: 'displaySmall',
                    displayXSmall: 'headingXSmall2',
                    displaySmall: 'displayXSmall',
                    masthead: 'displaySmall',
                    display: 'displayXSmall',
                    displayLarge: 'displayLarge',
                    displayXLarge: 'displaySmall',
                    ultrabold: 'displaySmall',
                    hero: 'headingMedium',
                }[theme] || 'headingMedium',
            themes: {
                displaySmallFixed: {},
                displaySmallToLarge1: {
                    large: 'displayLarge',
                },
                displayXSmall: {
                    large: 'displayXSmall',
                },
                displaySmall: {
                    large: 'displaySmall',
                    xLarge: 'displayMedium',
                },
                masthead: { large: 'displayLarge' },
                display: { large: 'displayLarge' },
                displayLarge: {
                    large: 'displayXLarge',
                },
                displayXLarge: {
                    medium: 'displayMedium',
                    large: 'displayXLarge',
                },
                ultrabold: {
                    large: 'displayMedium',
                },
                hero: {
                    large: 'headingXLarge',
                },
            }[theme] || { large: 'headingLarge' },
        },
    };

    const richTextHeadlineOverrides = {
        // This headline should always come in as a paragraph from the CMS.
        // It is not possible to restrict input to H2.
        renderMark: {
            [MARKS.BOLD]: text => <em>{text}</em>,
        },
        renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => {
                return (
                    <Text.Theme
                        config={textThemeConfig}
                        as="span"
                        className={styles.paragraph}
                    >
                        {children}
                    </Text.Theme>
                );
            },
            // Override for rBST link in product category index
            [INLINES.HYPERLINK]: node => {
                return (
                    <Link
                        target="_self"
                        href={node.data.uri}
                        className={styles.richTextLink}
                    >
                        {node.content[0].value}
                    </Link>
                );
            },
        },
    };

    if (typeof children === 'object') {
        return (
            <RichText
                overrides={richTextHeadlineOverrides}
                richText={children}
            />
        );
    }

    return (
        <Text.Theme config={textThemeConfig} as="span" fixWidows={fixWidows}>
            {children}
        </Text.Theme>
    );
};

Headline.propTypes = {
    theme: PropTypes.oneOf([
        'normal',
        'display',
        'masthead',
        'displayXSmall',
        'displaySmall',
        'displaySmallFixed',
        'displaySmallToLarge1',
        'displayLarge',
        'displayXLarge',
        'ultrabold',
        'makers-reserve',
        'hero',
    ]),
    children: PropTypes.any,
    fixWidows: PropTypes.bool,
};

const TextEyebrow = ({
    theme = 'normal',
    textAlign = 'center',
    eyebrow,
    headline,
    richHeadline,
    className,
    globalTheme,
    eyebrowColor,
    headlineColor,
    cta,
    ctaButton,
    fixWidows,
    firstBlock,
}) => {
    theme ??= 'normal';
    fixWidows ??= true;
    firstBlock ??= false;

    const hasHeadline = !!(headline || richHeadline);
    const HeadlineTag = firstBlock ? 'h1' : 'h2';

    return (
        <div
            className={cx(
                styles.root,
                className,
                styles[theme],
                styles[`theme--${globalTheme}`],
                {
                    [styles.textAlignLeft]: textAlign === 'left',
                }
            )}
            style={{
                '--eyebrow': eyebrowColor && getColor(eyebrowColor),
                '--headline': headlineColor && getColor(headlineColor),
            }}
        >
            {eyebrow && (
                <div
                    className={styles.innerTextWrapper}
                    style={{
                        color: 'var(--eyebrow)',
                    }}
                >
                    <Eyebrow
                        theme={theme}
                        firstBlock={firstBlock}
                        hasHeadline={hasHeadline}
                    >
                        {eyebrow}
                    </Eyebrow>
                </div>
            )}
            {(headline || richHeadline) && (
                <HeadlineTag
                    className={cx(styles.innerTextWrapper, styles.headline)}
                    style={{
                        color: 'var(--headline)',
                    }}
                >
                    <Headline theme={theme} fixWidows={fixWidows}>
                        {richHeadline || headline}
                    </Headline>
                </HeadlineTag>
            )}

            {(cta?.link || cta?.assetDownloadLink) && !ctaButton && (
                <div className={styles.ctaWrapper}>
                    <ButtonCTA className={styles.cta} {...cta} />
                </div>
            )}
            {(ctaButton?.link || ctaButton?.assetDownloadLink) && (
                <div className={styles.ctaWrapper}>
                    <ButtonCTA className={styles.cta} {...ctaButton} />
                </div>
            )}
        </div>
    );
};

TextEyebrow.propTypes = {
    className: PropTypes.string,
    cta: PropTypes.object,
    ctaButton: PropTypes.object,
    eyebrow: PropTypes.string,
    eyebrowColor: PropTypes.string,
    fixWidows: PropTypes.bool,
    globalTheme: PropTypes.string,
    headline: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    headlineColor: PropTypes.string,
    richHeadline: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    theme: PropTypes.oneOf([
        'normal',
        'display',
        'masthead',
        'displayXSmall',
        'displaySmall',
        'displaySmallFixed',
        'displaySmallToLarge1',
        'displayLarge',
        'displayXLarge',
        'ultrabold',
        'hero',
        'makers-reserve',
    ]),
    textAlign: PropTypes.oneOf(['center', 'left']),
    firstBlock: PropTypes.bool,
};

export default TextEyebrow;
